import React, { useState, useEffect, PropsWithChildren } from "react";
import { useNavigate } from "react-router-dom";
import regbanner from "../../../assets/icons/progresslogo.png";
import errorIcon from "../../../assets/icons/errorIcon.png";
import "./ElogVerificationInProgress.scss";
import Button from "../../UI/Button/Button";
import { AGetStageCompletionStatus } from "../../Services/registration/continueApiService";
import GlobalStore from "../../store/globalStore";
import {
  ADionPostData,
  AElogRegistration,
} from "../../Services/registration/elogApiService";
import { decryptJwtToken } from "../../Services/dashboard/encryptionNew";
import { ITokenObj } from "../../types/genericTypes";
import { containerUrl } from "../../utils/constants";

const ElogVerificationInProgress: React.FC<PropsWithChildren> = () => {
  const navigate = useNavigate();

  const [failed, setFailed] = useState(false);
  const [authUrlFailed, setAuthUrlFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    AGetStageCompletionStatus(
      (res: any) => getClientStageDetails(res),
      GlobalStore.applicationID
    );
  }, []);

  /**
   * Fetch public IP and store it globally.
   */

  /**
   * Check E-Log response.
   *
   * Sample:
   * {
   *   status: true,
   *   data: {
   *     StatusCode: "100",
   *     ErrorDesc: "SUCCESS",
   *     Param: [{ Clientcode, Statuscode: "100", Errordesc }]
   *   }
   * }
   *
   * E-Log is successful only when the top-level StatusCode
   * and ALL client entries in Param have status code 100.
   */
  const isElogSuccess = (elogRes: any): boolean => {
    if (!elogRes || elogRes.error || elogRes.status === false) {
      return false;
    }

    const data = elogRes?.data;

    const isCode100 = (code: any) => String(code ?? "").trim() === "100";

    if (!isCode100(data?.StatusCode ?? data?.statusCode)) {
      return false;
    }

    const param = data?.Param ?? data?.param;

    if (!Array.isArray(param) || param.length === 0) {
      return false;
    }

    return param.every((client: any) =>
      isCode100(client?.Statuscode ?? client?.statuscode)
    );
  };

  /**
   * E-Log registration.
   *
   * No URL / redirect / window.open logic here.
   * Response directly determines success/failure.
   */
  const handleElogRegistration = async (
    callback?: (success: boolean) => void
  ) => {
    try {
      const response = await fetch(
        "https://api.ipify.org?format=json"
      );

      const data = await response.json();

      const elogIp = data?.ip || "";

      if (!elogIp) {
        setAuthUrlFailed(true);
        setIsLoading(false);
        callback?.(false);
        return;
      }

      const elogObj = {
        ClientCode: GlobalStore.applicationID,
        FirstHolderIpAdd: elogIp,
        SecondHolderIpAdd: elogIp,
        ThirdHolderIpAdd: elogIp,
        EntitySubType: GlobalStore.entitySubType,
      };

      AElogRegistration(
        (elogRes: any) => {

          const success = isElogSuccess(elogRes);

          if (success) {
            callback?.(true);
            return;
          }

          setAuthUrlFailed(true);
          setIsLoading(false);

          callback?.(false);
        },
        elogObj
      );
    } catch (error) {
      console.error("Unable to get IP address:", error);

      setAuthUrlFailed(true);
      setIsLoading(false);

      callback?.(false);
    }
  };

  function getClientStageDetails(res: any) {
    if (!res || res.error) {

      setIsLoading(false);
      return;
    }

    if (res.data) {
      try {
        const responseObj = JSON.parse(res.data);

        /**
         * Already completed both Dion and E-Log.
         */
        if (
          responseObj?.isdioncompleted &&
          responseObj?.iselogcompleted
        ) {
          navigate(containerUrl.esignsubmit);
          return;
        }

        /**
         * Dion already completed,
         * E-Log still needs to be completed.
         *
         * Directly call Elog Registration.
         */
        if (
          responseObj?.isdioncompleted &&
          !responseObj?.iselogcompleted
        ) {
          handleElogRegistration((success) => {
            if (success) {
              navigate(containerUrl.esignsubmit);
            }
          });

          return;
        }

        /**
         * Normal flow.
         *
         * Dion → E-Log
         */
        if (responseObj?.elog_opted) {
          let userId = 0;

          const tokenObj: string = decryptJwtToken(
            GlobalStore?.token
          );

          if (
            tokenObj !== "" &&
            !!tokenObj &&
            tokenObj?.length > 0
          ) {
            const tokenMainObj: ITokenObj = JSON.parse(
              tokenObj
            ) as ITokenObj;

            userId = parseInt(
              tokenMainObj?.UserId?.toString()
            );
          }

          const dionPayload = {
            accountCategory: responseObj.accountcategory,
            applicationId: GlobalStore.applicationID,
            stage: "ELOG_VERIFICATION",
            userId: userId,
          };

          const delay = (ms: number) =>
            new Promise((resolve) =>
              setTimeout(resolve, ms)
            );

          /**
           * Existing E-Log call wrapped in Promise
           * so Dion retry logic remains unchanged.
           */
          const registerElog = () => {
            return new Promise<boolean>((resolve) => {
              handleElogRegistration((success) => {
                resolve(success);
              });
            });
          };

          ADionPostData(
            async (dionRes: any) => {
              if (!dionRes || dionRes.error) {
                console.error(
                  "Dion failed:",
                  dionRes
                );

                setTimeout(() => {
                  setFailed(true);
                  setIsLoading(false);
                }, 1000);

                return;
              }


              /**
               * Keep existing retry delays.
               */
              const retryDelays = [
                5000,
                7000,
                10000,
              ];

              let success = false;

              for (const delayMs of retryDelays) {
                await delay(delayMs);


                try {
                  const elogSuccess =
                    await registerElog();

                  if (elogSuccess) {
                    /**
                     * E-Log API succeeded.
                     * No URL / redirect from E-Log.
                     *
                     * Existing success navigation.
                     */
                    navigate(
                      containerUrl.esignsubmit
                    );

                    success = true;
                    break;
                  }

                  console.warn(
                    "E-Log registration failed. Will retry..."
                  );
                } catch (err) {
                  console.error(
                    "E-Log exception:",
                    err
                  );
                }
              }

              if (!success) {
                console.error(
                  "E-Log failed after all retries"
                );

                setAuthUrlFailed(true);
                setIsLoading(false);
              }
            },
            dionPayload
          );
        } else {
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Parsing error", err);
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }

  const handleRetry = () => {
    setFailed(false);
    setAuthUrlFailed(false);
    setIsLoading(true);

    AGetStageCompletionStatus(
      (res: any) => getClientStageDetails(res),
      GlobalStore.applicationID
    );
  };

  if (authUrlFailed) {
    return (
      <div
        className="docs-pending-root"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        <img
          src={errorIcon}
          alt="Error"
          className="verified-img"
        />

        <h2 className="status-heading">
          Unable to Proceed
        </h2>

        <span className="status-subHead">
          {
            "We’re experiencing technical difficulties. Please contact your RM for assistance."
          }
        </span>
      </div>
    );
  }

  if (failed) {
    return (
      <div
        className="docs-pending-root"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        <img
          src={errorIcon}
          alt="Error"
          className="verified-img"
        />

        <h2 className="status-heading">
          Unable to Proceed
        </h2>

        <span className="status-subHead">
          {
            "We’re experiencing technical difficulties. Please contact your RM for assistance."
          }
        </span>
      </div>
    );
  }

  return (
    <div
      className="docs-pending-root"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <img
        src={regbanner}
        className="verified-img"
        alt="Account status"
      />

      <div>
        <h2 className="status-heading">
          {isLoading && "Verification in Progress"}
        </h2>

        <span className="status-subHead">
          {isLoading &&
            "E-Log verification is in progress. Please wait while we complete the process."}
        </span>
      </div>
    </div>
  );
};

export default ElogVerificationInProgress;